package es.subastia.auctionlist

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import es.subastia.domain.model.AuctionProcedureType
import es.subastia.navigation.Navigator
import es.subastia.ui.components.FilterSelector
import es.subastia.ui.components.SubastiaButton
import es.subastia.ui.model.AuctionTypeUiModel
import es.subastia.ui.model.ProvinceUiModel
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.btn_apply_filters
import subastia.composeapp.generated.resources.btn_close
import subastia.composeapp.generated.resources.label_auction_type
import subastia.composeapp.generated.resources.label_procedure_type
import subastia.composeapp.generated.resources.label_province
import subastia.composeapp.generated.resources.option_all_masculine
import subastia.composeapp.generated.resources.title_filter_screen

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun AuctionFilterScreen(
    navigator: Navigator,
) {
    val viewModel: AuctionFilterViewModel = koinViewModel()
    val state by viewModel.state.collectAsStateWithLifecycle()

    AuctionFilterContent(
        state = state,
        onCloseClicked = { navigator.pop() },
        onProvinceSelected = viewModel::selectProvince,
        onTypeSelected = viewModel::selectType,
        onProcedureTypeSelected = viewModel::selectProcedureType,
        onApplyFilters = {
            viewModel.saveFilters()
            navigator.pop()
        },
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuctionFilterContent(
    state: AuctionFilterUiState,
    onCloseClicked: () -> Unit,
    onProvinceSelected: (ProvinceUiModel?) -> Unit,
    onTypeSelected: (AuctionTypeUiModel?) -> Unit,
    onProcedureTypeSelected: (AuctionProcedureType?) -> Unit,
    onApplyFilters: () -> Unit,
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.title_filter_screen)) },
                navigationIcon = {
                    IconButton(onClick = onCloseClicked) {
                        Icon(Icons.Default.Close, contentDescription = stringResource(Res.string.btn_close))
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.surface,
                    titleContentColor = MaterialTheme.colorScheme.onSurface,
                ),
            )
        },
    ) { padding ->
        Column(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
                .padding(16.dp)
                .verticalScroll(rememberScrollState()),
        ) {

            // Province Section
            FilterSelector(
                label = stringResource(Res.string.label_province),
                options = ProvinceUiModel.entries,
                selectedOption = state.selectedProvince,
                onOptionSelected = onProvinceSelected,
                optionLabel = { it.nm },
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(Modifier.height(24.dp))

            // Type Section
            FilterSelector(
                label = stringResource(Res.string.label_auction_type),
                options = AuctionTypeUiModel.entries,
                selectedOption = state.selectedType,
                onOptionSelected = onTypeSelected,
                optionLabel = { stringResource(it.textRes) },
                allOptionLabel = stringResource(Res.string.option_all_masculine),
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(Modifier.height(24.dp))

            // Procedure Type Section
            FilterSelector(
                label = stringResource(Res.string.label_procedure_type),
                options = AuctionProcedureType.entries.filter { it != AuctionProcedureType.UNKNOWN },
                selectedOption = state.selectedProcedureType,
                onOptionSelected = onProcedureTypeSelected,
                optionLabel = { it.label },
                allOptionLabel = stringResource(Res.string.option_all_masculine),
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(Modifier.weight(1f))

            SubastiaButton(
                text = stringResource(Res.string.btn_apply_filters),
                onClick = onApplyFilters,
            )
        }
    }
}

@Composable
fun FilterSectionTitle(title: String) {
    Text(
        text = title,
        style = MaterialTheme.typography.titleMedium,
        fontWeight = FontWeight.Bold,
        modifier = Modifier.padding(bottom = 8.dp),
    )
}

@Preview
@Composable
fun AuctionFilterScreenPreview() {
    MaterialTheme {
        AuctionFilterContent(
            state = AuctionFilterUiState(),
            onCloseClicked = {},
            onProvinceSelected = {},
            onTypeSelected = {},
            onProcedureTypeSelected = {},
            onApplyFilters = {},
        )
    }
}
